import { h, Fragment, FunctionComponent } from 'preact';
import { Trans } from '@lingui/macro';
import { useCallback } from 'preact/hooks';
import { Link } from 'preact-router/match';
import useEmblaCarousel from 'embla-carousel-react';
import { TournamentSummary } from '../../../services/slot-tournaments'; // Assuming path is correct
import VictoryVaultTournamentCard from './VictoryVaultTournamentCard';

// Props definition for the carousel
interface TournamentCarouselProps {
    title: string;
    tournaments: TournamentSummary[];
}

// Simple card component for displaying tournament info within the carousel
const TournamentCard: FunctionComponent<{ tournament: TournamentSummary }> = ({ tournament }) => (
    <a href={`/slot-tournaments/${tournament.id}`} key={tournament.id} class="flex flex-col min-w-0 bg-gray-50 hover:bg-gray-100 rounded-lg shadow p-4 mx-2 border border-gray-200 transition duration-200 ease-in-out transform grow-0 shrink-0 basis-48"> {/* Narrower card */}
        <h3 class="text-lg font-semibold text-gray-900 mb-2 truncate">{tournament.title}</h3> {/* Dark text */}
        <p class="text-gray-600 text-sm mb-3 grow">{tournament.description}</p> {/* Dark text */}
        <div class="flex justify-between items-center text-xs text-gray-500 mt-auto"> {/* Dark text */}
            <span>
                <Trans>Starts</Trans>: {new Date(tournament.start_date).toLocaleDateString()}
            </span>
            <span>
                <Trans>Ends</Trans>: {new Date(tournament.end_date).toLocaleDateString()}
            </span>
        </div>
    </a>
);

export const TournamentCarousel: FunctionComponent<TournamentCarouselProps> = ({ title, tournaments }) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: 'start',
        containScroll: 'trimSnaps',
        loop: false, // Loop might not be ideal if there are few items
        skipSnaps: false,
    });

    const scrollPrev = useCallback(() => {
        if (emblaApi) emblaApi.scrollPrev();
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
        if (emblaApi) emblaApi.scrollNext();
    }, [emblaApi]);

    if (!tournaments || tournaments.length === 0) {
        return (
            <div class="mb-8">
                <h2 class="text-2xl">{window.Translation({title})} </h2>
                <p class="text-gray-500"><Trans>No tournaments found for this category.</Trans></p>
            </div>
        );
    }

    return (
        <div class="mb-8 relative">
            {/* Title and Buttons Container */}
            <div class="flex justify-between items-start mb-4">
                <h2 class="text-2xl">{window.Translation({title})} </h2>
                {/* Buttons positioned top-right */}
                <div class="flex space-x-2 z-10">
                    {emblaApi?.canScrollPrev() && (
                        <button class="text-gray-500 hover:text-gray-800 p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-300" onClick={scrollPrev} aria-label="Previous Tournament">
                            {/* Smaller SVG Left Arrow */}
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
                        </button>
                    )}
                    {emblaApi?.canScrollNext() && (
                        <button class="text-gray-500 hover:text-gray-800 p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-300" onClick={scrollNext} aria-label="Next Tournament">
                            {/* Smaller SVG Right Arrow */}
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
                        </button>
                    )}
                </div>
            </div>
            {/* Carousel */}
            <div class="embla" ref={emblaRef}>
                <div class="embla__container flex">
                    {tournaments.map((tournament) => {
                        if (tournament.title.startsWith('Victory Vault')) {
                            return <VictoryVaultTournamentCard tournament={tournament} key={tournament.id} />;
                        }
                        return <TournamentCard tournament={tournament} key={tournament.id} />;
                    })}
                </div>
            </div>
        </div>
    );
};
